/**
 * Centralized Cloudinary URL helper for consistent photo display.
 * IMPORTANT: The cloudinary_url already contains rotation/flip transformations (e.g., a_90).
 * We must NOT rebuild these - only add resizing when needed.
 */

/**
 * Insert resize transformations into an existing Cloudinary URL.
 * Preserves any existing transformations (rotation, flip, etc.).
 * 
 * @param {string} url - Existing Cloudinary URL (may already have transformations)
 * @param {Object} options - Resize options
 * @returns {string} - URL with resize transformations added
 */
function insertResizeTransform(url, options = {}) {
  if (!url || !url.includes('/upload/')) {
    return url;
  }
  
  const { width, height, crop = 'fill', gravity = 'auto' } = options;
  
  if (!width && !height) {
    return url;
  }
  
  // Build resize transformation string
  const resizeParts = [];
  if (crop) resizeParts.push(`c_${crop}`);
  if (gravity && crop === 'fill') resizeParts.push(`g_${gravity}`);
  if (width) resizeParts.push(`w_${width}`);
  if (height) resizeParts.push(`h_${height}`);
  const resizeStr = resizeParts.join(',');
  
  // Insert resize AFTER /upload/ but BEFORE any existing transformations
  // This way existing transformations (like a_90) are preserved and applied after resize
  return url.replace('/upload/', `/upload/${resizeStr}/`);
}

/**
 * Get the display URL for a photo.
 * For Cloudinary photos: uses cloudinary_url directly (contains rotation).
 * For local photos: falls back to base64 data.
 * 
 * @param {Object} photo - Photo object
 * @returns {string|null}
 */
export function getPhotoUrl(photo) {
  if (!photo) return null;
  
  // If it's a string, return as-is
  if (typeof photo === 'string') return photo;
  
  // Prefer cloudinary_url (already has transformations)
  if (photo.cloudinary_url) {
    return photo.cloudinary_url;
  }
  
  // Fallback to base64 for local photos
  if (photo.image_data) {
    return `data:image/jpeg;base64,${photo.image_data}`;
  }
  
  return null;
}

/**
 * Get thumbnail URL for a photo.
 * Adds resize transformation to existing cloudinary_url (preserves rotation).
 * 
 * @param {Object} photo - Photo object
 * @param {number} size - Thumbnail size (default: 150)
 * @returns {string|null}
 */
export function getThumbnailUrl(photo, size = 150) {
  if (!photo) return null;
  
  const baseUrl = getPhotoUrl(photo);
  if (!baseUrl) return null;
  
  // For base64 images, return as-is (can't resize)
  if (baseUrl.startsWith('data:')) {
    return baseUrl;
  }
  
  // For Cloudinary URLs, add resize transformation
  return insertResizeTransform(baseUrl, {
    width: size,
    height: size,
    crop: 'fill',
    gravity: 'auto'
  });
}

/**
 * Get medium-sized URL for album grids, cards, etc.
 * @param {Object} photo - Photo object
 * @param {number} width - Width (default: 400)
 * @param {number} height - Height (default: 300)
 * @returns {string|null}
 */
export function getMediumUrl(photo, width = 400, height = 300) {
  if (!photo) return null;
  
  const baseUrl = getPhotoUrl(photo);
  if (!baseUrl) return null;
  
  if (baseUrl.startsWith('data:')) {
    return baseUrl;
  }
  
  return insertResizeTransform(baseUrl, {
    width,
    height,
    crop: 'fill',
    gravity: 'auto'
  });
}

/**
 * Get full-size URL for lightbox/modal display.
 * Returns the cloudinary_url directly (no additional transformations).
 * 
 * @param {Object} photo - Photo object
 * @returns {string|null}
 */
export function getFullSizeUrl(photo) {
  if (!photo) return null;
  
  // Return the URL directly - it already contains any rotation/flip
  return getPhotoUrl(photo);
}

/**
 * Get cover photo URL for articles/albums
 * @param {Object} photo - Photo object
 * @param {number} width - Width (default: 1200)
 * @param {number} height - Height (default: 630)
 * @returns {string|null}
 */
export function getCoverUrl(photo, width = 1200, height = 630) {
  if (!photo) return null;
  
  const baseUrl = getPhotoUrl(photo);
  if (!baseUrl) return null;
  
  if (baseUrl.startsWith('data:')) {
    return baseUrl;
  }
  
  return insertResizeTransform(baseUrl, {
    width,
    height,
    crop: 'fill',
    gravity: 'auto'
  });
}

/**
 * Legacy function for compatibility - same as getPhotoUrl with options
 * @deprecated Use getThumbnailUrl, getMediumUrl, or getFullSizeUrl instead
 */
export function getCloudinaryUrl(photo, options = {}) {
  if (!photo) return null;
  
  const baseUrl = getPhotoUrl(photo);
  if (!baseUrl) return null;
  
  if (baseUrl.startsWith('data:')) {
    return baseUrl;
  }
  
  const { width, height, crop, gravity } = options;
  if (width || height) {
    return insertResizeTransform(baseUrl, { width, height, crop, gravity });
  }
  
  return baseUrl;
}

export default {
  getPhotoUrl,
  getCloudinaryUrl,
  getThumbnailUrl,
  getMediumUrl,
  getFullSizeUrl,
  getCoverUrl
};
